/**
 * Mock API for testing mockup upload functionality
 * Enable MOCK_MODE to test upload flow without waiting for real AI processing
 *
 * To enable: Set MOCK_MODE = true
 * To disable: Set MOCK_MODE = false (use real API)
 */

// Toggle this to enable/disable mock mode
export const MOCK_MODE = false;

// Mock timing configuration (in milliseconds)
const MOCK_PROCESSING_TIME = 5000; // 5 seconds total processing time

// Mock result data that matches real API response
const MOCK_RESULT = {
  success: true,
  jobId: null, // Will be set dynamically
  mockupImages: [
    {
      id: 'mock-image-1',
      url: 'https://via.placeholder.com/800x600/9333EA/FFFFFF?text=Mockup+1',
      segmentData: {
        walls: [
          { id: 'wall-1', coordinates: [[0, 0], [800, 0], [800, 600], [0, 600]] }
        ]
      }
    },
    {
      id: 'mock-image-2',
      url: 'https://via.placeholder.com/800x600/7C33EA/FFFFFF?text=Mockup+2',
      segmentData: {
        walls: [
          { id: 'wall-2', coordinates: [[0, 0], [800, 0], [800, 600], [0, 600]] }
        ]
      }
    }
  ],
  originalImage: {
    id: 'mock-original',
    url: 'https://via.placeholder.com/800x600/CCCCCC/333333?text=Original+Room'
  },
  metadata: {
    processingTime: 5000,
    aiModel: 'mock-model-v1',
    wallsDetected: 2
  }
};

/**
 * Mock implementation of uploadRoomMockup
 * Returns immediately with 202 Accepted response
 */
export const mockUploadRoomMockup = async (formData) => {
  console.log('[MOCK API] Upload started with formData:', formData);

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: {
      success: true,
      message: 'Upload accepted for processing',
      jobId: formData.get('jobId') || 'mock-job-id',
      status: 'pending'
    }
  };
};

/**
 * Mock implementation of pollMockupStatus
 * Simplified to just pending → completed (no intermediate statuses)
 *
 * @param {string} jobId - The job ID to poll
 * @param {function} onProgress - Callback for progress updates
 * @returns {Promise} Promise that resolves with final result
 */
export const mockPollMockupStatus = (jobId, onProgress) => {
  let cancelled = false;
  let timeoutId = null;

  console.log('[MOCK API] Starting mock polling for jobId:', jobId);

  const promise = new Promise((resolve, reject) => {
    // Send initial 'pending' status
    if (onProgress && typeof onProgress === 'function') {
      onProgress({
        jobId,
        status: 'pending',
        progress: 0
      });
    }

    // After MOCK_PROCESSING_TIME, complete the job
    timeoutId = setTimeout(() => {
      if (cancelled) {
        reject(new Error('Polling cancelled'));
        return;
      }

      console.log('[MOCK API] Upload completed');

      // Return mock result with actual jobId
      const result = {
        ...MOCK_RESULT,
        jobId
      };

      resolve(result);
    }, MOCK_PROCESSING_TIME);
  });

  // Add cancel method to promise
  promise.cancel = () => {
    console.log('[MOCK API] Polling cancelled for jobId:', jobId);
    cancelled = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return promise;
};

/**
 * Mock implementation that simulates a failure
 * Useful for testing error states
 */
export const mockPollMockupStatusWithFailure = (jobId, onProgress, failureDelay = 2500) => {
  let cancelled = false;
  let timeoutId = null;

  console.log('[MOCK API] Starting mock polling with failure after:', failureDelay, 'ms');

  const promise = new Promise((resolve, reject) => {
    // Send initial 'pending' status
    if (onProgress && typeof onProgress === 'function') {
      onProgress({
        jobId,
        status: 'pending',
        progress: 0
      });
    }

    // After failureDelay, fail the job
    timeoutId = setTimeout(() => {
      if (cancelled) {
        reject(new Error('Polling cancelled'));
        return;
      }

      console.log('[MOCK API] Mock failure triggered');
      reject(new Error('Mock processing failure'));
    }, failureDelay);
  });

  // Add cancel method to promise
  promise.cancel = () => {
    console.log('[MOCK API] Polling cancelled for jobId:', jobId);
    cancelled = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return promise;
};

export default {
  MOCK_MODE,
  mockUploadRoomMockup,
  mockPollMockupStatus,
  mockPollMockupStatusWithFailure
};
